import React, { ReactNode } from "react";
const Highlighter = require('react-highlight-words');

import { mode as getMode, stdDev as getStdDev } from "@common/lib/stats";
import { escape } from "@common/util";
import { TableBlock } from "../../types";
import {
  ATTR_FONT_SIZE,
  ATTR_RGB,
  TAG_BOLD,
  TAG_BREAK,
  TAG_COLOR,
  TAG_FONT,
  TAG_ITALICS,
  TAG_NON_EXTRACT,
  TAG_SUBSCRIPT,
  TAG_SUPERSCRIPT,
  TAG_TCY,
  TAG_UNDERLINE,
} from "./constants";
import { FontClass, IFontInformation } from "./types";

const escapeXMLEntities = (input: string): string =>
  input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/**
 * Maps the contents of an Innodom element to a pretty-printed de-namespaced XML string.
 * @param block  Element The input element to extract contents from.
 * @param indent string An indentation string.
 * @return string Returns the contents of the element in a normalized, easier to edit XML string.
 */
export const extractFragmentFromBlock = (block: Element): string => {
  const toReturn: Array<string> = [];
  const children = Array.from(block.childNodes);
  for (const child of children) {
    if (child instanceof Element) {
      let name = child.nodeName;
      if (name.indexOf("inno:") >= 0) {
        name = name.substring(5);
      }
      let attrs = "";
      const childAttrs = Array.from(child.attributes).filter(attr => attr.name !== "xmlns:inno");
      if (childAttrs.length > 0) {
        attrs = " " + childAttrs.map(attr => `${attr.name}="${escape(attr.value)}"`).join(" ");
      }
      let hasInnerContent = false;
      for (const innerContent of Array.from(child.childNodes)) {
        if (innerContent instanceof Element) {
          hasInnerContent = true;
          break;
        } else if (innerContent.textContent) {
          if (innerContent.textContent.trim().length > 0) {
            hasInnerContent = true;
            break;
          }
        }
      }
      if (hasInnerContent) {
        toReturn.push(
          `<${name}${attrs}>${extractFragmentFromBlock(
            child,
          )}</${name}>`,
        );
      } else {
        toReturn.push(`<${name}${attrs} />`);
      }
      continue;
    }
    const text = (child.textContent || "");
    if (text.length > 0) {
      toReturn.push(escapeXMLEntities(text));
    }
  }
  return toReturn.join("");
};

/**
 * Injects the user-friendly XML fragment back into the Innodom document.
 * @param block  Element The block to insert into.
 * @param fragment string The XML string to insert.
 * @return Element Returns the block after insertion.
 */
export const insertFragmentIntoBlock = (block: Element, fragment: string): Element => {
  block.innerHTML = fragment;
  // map the inner tags to inno: equivalents
  const newInnerHTML: Array<string> = [];
  const children = Array.from(block.childNodes);
  for (const child of children) {
    if (child instanceof Element) {
      let name = child.nodeName;
      if (name.indexOf("inno:") !== 0) {
        name = `inno:${name}`;
      }
      let attrs = ' xmlns:inno="http://innodatalabs.com/innodom"';
      if (child.attributes.length > 0) {
        attrs +=
          " " +
          Array.from(child.attributes)
            .map(attr => `${attr.name}="${attr.value}"`)
            .join(" ");
      }
      const innerContents = insertFragmentIntoBlock(child, child.innerHTML);
      newInnerHTML.push(`<${name}${attrs}>${innerContents.innerHTML}</${name}>`);
      continue;
    } else if (child.textContent) {
      newInnerHTML.push(escapeXMLEntities(child.textContent));
    }
  }
  block.innerHTML = newInnerHTML.join("");
  return block;
};

/**
 * Maps a font size string like "12.00pt" to just the number 12.
 * @param el  The element with the font-size attribute.
 * @return number Returns the number associated with the font.
 */
export const extractSizeFromFontElement = (el: Element): number =>
  parseFloat((el.getAttribute(ATTR_FONT_SIZE) || "").trim().replace(/pt$/, ""));

/**
 * Extracts some font metrics from the root element.
 * @param root Element The root element to use to extract font metrics.
 * @return IFontInformation | undefined. Returns an IFontInformation structure or undefined if no
 *         <inno:font> tags were present at all.
 */
export const extractFontInformation = (root: Element): IFontInformation | undefined => {
  const fontTags = root.getElementsByTagName(TAG_FONT);
  const fontSizes: Array<number> = [];
  // tslint:disable-next-line: prefer-for-of
  for (let i = 0; i < fontTags.length; i++) {
    const fontSize = extractSizeFromFontElement(fontTags[i]);
    if (!isNaN(fontSize)) {
      fontSizes.push(fontSize);
    }
  }
  const mode = getMode(fontSizes);
  if (mode === undefined) {
    return undefined;
  }
  const stdDev = getStdDev(fontSizes);
  if (stdDev === undefined) {
    return { lowerBound: mode, mode, upperBound: mode };
  }
  return { lowerBound: mode - 2 * stdDev, mode, upperBound: mode + 2 * stdDev };
};

/**
 * For the given size and font information, returns the associated FontClass variant.
 * @param size number The font size as a number.
 * @param fontInformation IFontInformation The font metrics from the document.
 * @return FontClass Returns one of the FontClassvariants.
 */
export const mapFontSizeToClass = (size: number, fontInformation: IFontInformation): FontClass => {
  if (isNaN(size) || size === fontInformation.mode) {
    return FontClass.Normal;
  }
  if (size > fontInformation.mode) {
    return size > fontInformation.upperBound ? FontClass.Larger : FontClass.Large;
  }
  return size < fontInformation.lowerBound ? FontClass.Smaller : FontClass.Small;
};

/**
 * Extract text from the <inno:text> tag. Possible sub tags include:
 * <br> line break
 * <i> italics
 * <b> bold
 * <u> underlined
 * <sup> superscript
 * <sub> subscript
 * <tcy> weird chinese thing
 * <font> font information
 * <color> text foreground
 * <nec> non-extractable
 * @param el Element The <ns0:text> element.
 * @param keyPrepend A key to prend for unique React keys.
 * @return Array<ReactNode> Returns a renderable list of React nodes.
 */
export const extractTextBlock = (
  el: Element,
  highlights?: Array<string> | undefined
): Array<ReactNode> => {
 
  var text = extractHumanReadableText(el);

  return [
    <Highlighter key={el.nodeName}
      caseSensitive="true"
      searchWords={!!highlights ? highlights : []}
      autoEscape={true}
      textToHighlight={text}
    />
  ]
};

export const stripNamespaceAttributes = (root: Element) => {
  if (root.nodeName !== "inno:dom") {
    root.removeAttribute("xmlns:inno");
  }
  const children = Array.from(root.childNodes).filter(c => c instanceof Element);
  for (const child of children) {
    stripNamespaceAttributes(child as Element);
  }
};

export const browserSupportsEditing = (): boolean => {
  const xml = `<?xml version="1.0" encoding="UTF-8"?><block />`;
  const parsed = new DOMParser().parseFromString(xml, "text/xml");
  const example = parsed.getElementsByTagName("block")[0];
  example.innerHTML = "<contents>testing</contents>";
  return example.childNodes.length > 0;
};

export const tableContainsCellRef = (block: TableBlock, id: string): boolean =>
  block.cells.some(r => r.includes(id));

export const extractParagraphFragments = (element : Element): Array<string> => {
  const paragraphFragments: Array<string> = []; 
  const { childNodes } = element; 
  for (let i = 0; i < childNodes.length; i++) {
    const child = childNodes[i];
    if (child.nodeName === "inno:br") {
      paragraphFragments.push(" ");
      continue;
    }
    if (child instanceof Element) {
      const innerContents = extractParagraphFragments(child);
      if (innerContents.length > 0) {
        innerContents.map((element) => {
          paragraphFragments.push(element)
        });
      }      
      continue;
    } 
    if (child.textContent !== null && child?.textContent.trim().length > 0){
      paragraphFragments.push(child.textContent);
    // Significant space can exist which will be a single space.
    // anything more than a single space can be assumed to be formatting spacing
    } else if (child.textContent == " ") {
      paragraphFragments.push(child.textContent);
    }
  }
  return paragraphFragments;
};

export const extractHumanReadableText = (element : Element): string => {
  let readabletext: string = "";
  const paragraphFragments = extractParagraphFragments(element);
  paragraphFragments.forEach(fragment => {
    readabletext = readabletext.concat("", fragment)
  });
  return readabletext.trim();
};